import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import classNames from 'classnames';
import { FormApi } from 'final-form';
import arrayMutators from 'final-form-arrays';
import { useBeaconRemove, useIsMobile, useNewTabOpen } from 'Hooks/Common';
import { Document, DocumentTypes, DocumentValues, Signer } from 'Interfaces/Document';
import { RoutePaths } from 'Interfaces/RoutePaths';
import { orderBy } from 'lodash-es';
import StorageService from 'Services/Storage';
import { FRONTEND_URL } from 'Utils/constants';
import { selectDocument } from 'Utils/selectors';

import { UploaderFormOptions, UploaderPageTypes } from './common/constant';
import { LandingDocumentForm } from './components';

const pageParam = 'pageType';

const LandingPageUploader = () => {
  useBeaconRemove();
  const isMobile = useIsMobile();
  const location = useLocation();

  const formRef = useRef<FormApi<DocumentValues> | null>(null);

  const [documentId, setDocumentId] = useState<Document['id'] | undefined>(undefined);
  const document = useSelector(state => selectDocument(state, { documentId }));

  const [openNewTab] = useNewTabOpen();

  const pageType = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);

    switch (searchParams.get(pageParam)) {
      case UploaderPageTypes.BLOG:
        return UploaderPageTypes.BLOG;
      case UploaderPageTypes.MAKER:
        return UploaderPageTypes.MAKER;
      default:
        return UploaderPageTypes.DEFAULT;
    }
  }, [location.search]);

  const initialValues = useMemo(() => {
    return {
      type: DocumentTypes.ME,
      signers: [
        {
          name: 'Me (Now)',
          email: 'example@example.com',
          order: -1,
          isPreparer: true,
        },
      ],
    } as DocumentValues;
  }, []);

  const isUploadedFile = useMemo(
    () =>
      document &&
      document.parts.length &&
      document.parts.every(part => part.filesUploaded),
    [document],
  );

  const formattedInitialValues = useMemo(() => {
    const documentSigners = ((document?.signers.length || 0) >=
    (initialValues.signers?.length || 0)
      ? document?.signers
      : initialValues.signers) as Signer[];
    const recipients = initialValues?.recipients
      ? initialValues?.recipients
      : document?.recipients;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { fields, ...restDocument } = document || {};

    return {
      ...initialValues,
      ...restDocument,
      signers: orderBy(documentSigners, 'order', 'asc'),
      isOrdered: document?.isOrdered || initialValues.isOrdered,
      type: initialValues.type,
      recipients,
      title: document?.title,
      message: document?.message,
    } as DocumentValues;
  }, [document, initialValues]);

  const handleOpenInteract = useCallback(() => {
    if (!documentId) return;

    const tempToken = StorageService.getAccessToken('tempToken');

    const link = `${FRONTEND_URL}${RoutePaths.LANDING_DOCUMENT_EDITOR.replace(
      ':documentId',
      documentId,
    )}?tempToken=${tempToken}`;

    openNewTab(link);
  }, [documentId, openNewTab]);

  useEffect(() => {
    if (isUploadedFile) {
      handleOpenInteract();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUploadedFile]);

  return (
    <div className={classNames('sign-up-landing__wrapper', { [pageType]: pageType })}>
      <Form
        initialValues={formattedInitialValues}
        onSubmit={() => {}}
        mutators={{ ...arrayMutators }}
        render={(renderProps: FormRenderProps<DocumentValues>) => {
          formRef.current = renderProps.form;

          return (
            <form
              className={classNames('sign-up-landing__upload-form', {
                mobile: isMobile,
                [pageType]: pageType,
              })}
            >
              <LandingDocumentForm
                {...renderProps}
                document={document}
                onDocumentCreate={setDocumentId}
                reopenInteract={handleOpenInteract}
                {...UploaderFormOptions[pageType]}
              />
            </form>
          );
        }}
      />
    </div>
  );
};

export default LandingPageUploader;
